import React, { useState } from 'react';
import { Job, JobStatus } from '../types';
import { authFetch } from '../api';
import { Icons } from '../constants';
import LeafletMap from './LeafletMap';

interface QuickAddJobModalProps {
  onClose: () => void;
  onAddJob: (job: Job) => void;
}

const DEFAULT_CENTER = { lat: 32.4609, lng: -84.9877 };

export default function QuickAddJobModal({ onClose, onAddJob }: QuickAddJobModalProps) {
  const [clientName, setClientName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [scheduledDate, setScheduledDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('Pro Bono / Veteran');
  const [location, setLocation] = useState<{lat: number, lng: number} | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attachments, setAttachments] = useState<string[]>([]);

  const handleLocationSelect = (lat: number, lng: number, addr: string) => {
    setLocation({ lat, lng });
    setAddress(addr);
  };

  const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const files = Array.from(e.target.files);
    
    const newAttachments = await Promise.all(files.map(file => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);


    const newJob: Job = {
      id: Math.random().toString(36).substr(2, 9),
      clientName,
      email: email || '',
      phone: phone || '',
      address,
      serviceType: 'Pruning', // Default or could be selectable
      status: JobStatus.SCHEDULED,
      createdAt: new Date().toISOString(),
      scheduledDate,
      crewId: '',
      inventory: [],
      lineItems: [],
      totalPrice: 0, // Pro bono default
      safetyChecked: false,
      documents: attachments.map(url => ({
        id: Math.random().toString(36).substr(2, 9),
        title: 'Attachment',
        type: 'Other',
        summary: 'Uploaded attachment',
        dateAdded: new Date().toISOString(),
        url
      }))
    };


    // Add notes as a line item or just keep it in the job (we don't have a notes field on Job, but we can add a 0 cost line item for notes)
    if (notes) {
      newJob.lineItems.push({
        id: Math.random().toString(36).substr(2, 9),
        description: notes,
        quantity: 1,
        unitPrice: 0
      });
    }

    // Try syncing to google calendar
    try {
      await authFetch('/api/workspace/calendar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `GreenTree: ${newJob.serviceType} - ${newJob.clientName}`,
          description: `Location: ${newJob.address}\nNotes: ${notes}`,
          date: newJob.scheduledDate,
          attendees: [newJob.email].filter(Boolean)
        })
      });
    } catch (e) {
      console.warn('Google Calendar sync failed. Might not be connected yet.', e);
    }

    onAddJob(newJob);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="p-6 border-b border-stone-100 flex justify-between items-center bg-stone-50">
          <div>
            <h2 className="text-2xl font-bold text-bark">Quick Add Job (Approved & Scheduled)</h2>
            <p className="text-stone-500 text-sm">Create a job directly without going through the lead process.</p>
          </div>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <Icons.Close />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 flex flex-col md:flex-row gap-6">
          <form id="quick-add-form" onSubmit={handleSubmit} className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Client Name *</label>
              <input 
                type="text" 
                value={clientName} 
                onChange={e => setClientName(e.target.value)}
                className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                placeholder="e.g. John Doe"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Email (Optional)</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)}
                  className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Phone (Optional)</label>
                <input 
                  type="tel" 
                  value={phone} 
                  onChange={e => setPhone(e.target.value)}
                  className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                  placeholder="555-0199"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Address *</label>
              <input 
                type="text" 
                value={address} 
                onChange={e => setAddress(e.target.value)}
                className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                placeholder="Click on the map or type address"
              />
              <p className="text-xs text-stone-500 mt-1">Street View and Satellite images will be automatically added to the job documents.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Scheduled Date</label>
                <input 
                  type="date" 
                  value={scheduledDate} 
                  onChange={e => setScheduledDate(e.target.value)}
                  className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Notes / Tags</label>
                <input 
                  type="text" 
                  value={notes} 
                  onChange={e => setNotes(e.target.value)}
                  className="w-full p-3 border border-stone-200 rounded-xl focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                  placeholder="e.g. Pro Bono, Veteran"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Attachments (Photos/Diagrams)</label>
              <input 
                type="file" 
                multiple 
                accept="image/*"
                onChange={handleAttachmentUpload}
                className="w-full p-2 bg-stone-50 rounded-lg border border-stone-200 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gecko/10 file:text-gecko hover:file:bg-gecko/20 transition"
              />
              {attachments.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {attachments.map((src, idx) => (
                    <div key={idx} className="relative group">
                      <img src={src} alt="Attachment" className="h-16 w-full object-cover rounded-lg border border-stone-200" />
                      <button 
                        type="button"
                        onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>

          <div className="flex-1 flex flex-col">
            <label className="block text-sm font-medium text-stone-700 mb-1">Select Location on Map</label>
            <div className="flex-1 min-h-[300px] bg-stone-100 rounded-xl overflow-hidden border border-stone-200 relative">
              <LeafletMap center={location || DEFAULT_CENTER} onSelect={handleLocationSelect} heightClass="h-[300px]" />
            </div>
            <p className="text-xs text-stone-500 mt-2 text-center">Click anywhere on the map to drop a pin and set the address.</p>
          </div>
        </div>

        <div className="p-6 border-t border-stone-100 bg-stone-50 flex justify-end space-x-4">
          <button 
            type="button"
            onClick={onClose}
            className="px-6 py-3 rounded-xl font-bold text-stone-600 hover:bg-stone-200 transition-colors"
          >
            Cancel
          </button>
          <button 
            type="submit"
            form="quick-add-form"
            disabled={isSubmitting || !clientName || !address}
            className="px-6 py-3 rounded-xl font-bold bg-palm text-white hover:bg-gecko transition-colors disabled:opacity-50 flex items-center space-x-2"
          >
            {isSubmitting ? 'Creating...' : 'Create Scheduled Job'}
          </button>
        </div>
      </div>
    </div>
  );
}
